import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useActiveLeague } from '../hooks/useActiveLeague';
import { useGetMatchSummary, useGetMatchPlayerStats } from "../api/matchesApi";

import MatchHeader from "../components/match/MatchHeader";
import MatchSharePanel from "../components/match/MatchSharePanel";
import MatchTabs, { MATCH_TAB } from "../components/match/MatchTabs";
import { PlayersList } from "../components/match/PlayersList";
import { CommentaryRow } from "../components/match/CommentariesBlock";
import { fanOutNarrative, getCommentaryKey } from "../utils/fanOutNarrative";
import {
    FEED_FILTER,
    buildPeriodBandIdentitySet,
    deriveScorers,
    getFeedEventIdentity,
    isAbsorbedFeedEvent
} from "../utils/matchFeed";
import { buildMatchLink } from "../utils/leagueLinks";

// Header compaction uses the same hysteresis as the live match page so a
// scroll position near the threshold cannot make the scoreboard flicker.
const HEADER_COMPACT_SCROLL_ENTER_PX = 90;
const HEADER_COMPACT_SCROLL_EXIT_PX = 30;

// Mirrors MatchPage's kickoff formatter; kept local so the pages stay decoupled.
const formatKickoffText = (rawDate) => {
    if (!rawDate) return 'Date TBD';
    const kickoff = new Date(rawDate);
    if (Number.isNaN(kickoff.getTime())) return 'Date TBD';
    return kickoff.toLocaleString();
};

// The summary always reads top to bottom: kick-off first, full-time last.
const compareCommentaryEventsChronologically = (a, b) => {
    if (a?.sequence_number != null && b?.sequence_number != null) {
        return a.sequence_number - b.sequence_number;
    }

    const aTimestamp = a?.timestamp ? new Date(a.timestamp).getTime() : NaN;
    const bTimestamp = b?.timestamp ? new Date(b.timestamp).getTime() : NaN;
    if (Number.isFinite(aTimestamp) && Number.isFinite(bTimestamp) && aTimestamp !== bTimestamp) {
        return aTimestamp - bTimestamp;
    }

    return (a?.minute || 0) - (b?.minute || 0);
};

function MatchSummary() {
    const { activeLeague } = useActiveLeague();
    const { slug, id, teamId } = useParams();
    const navigate = useNavigate();
    const routeLeagueSlug = slug || activeLeague?.slug || null;
    const routeBase = routeLeagueSlug ? `/${routeLeagueSlug}` : "";
    const matchesLink = routeBase ? `${routeBase}/matches` : '#';

    // Fetch core summary (commentary, basic info)
    const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useGetMatchSummary(id);
    // Fetch detailed player stats (new endpoint)
    const { data: playerStatsData, isLoading: statsLoading, error: statsError } = useGetMatchPlayerStats(id);

    const isLoading = summaryLoading || statsLoading;
    const error = summaryError || statsError;

    const [isHeaderCompact, setIsHeaderCompact] = useState(false);
    const [feedFilter, setFeedFilter] = useState(FEED_FILTER.ALL);

    // Compact the sticky scoreboard once the page is scrolled, with hysteresis
    // so content crossing the threshold cannot make it flicker.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            return undefined;
        }

        const handleScroll = () => {
            const offset = window.scrollY || window.pageYOffset || 0;
            setIsHeaderCompact((wasCompact) => {
                if (!wasCompact && offset > HEADER_COMPACT_SCROLL_ENTER_PX) return true;
                if (wasCompact && offset < HEADER_COMPACT_SCROLL_EXIT_PX) return false;
                return wasCompact;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Saved commentary stores one event per moment; fan the narrative parts out
    // into the same rows the live feed renders, oldest first, and drop the
    // SCORE_UPDATE rows the goal hero card already absorbs.
    const fannedEvents = useMemo(() => {
        const commentary = Array.isArray(summaryData?.commentary) ? summaryData.commentary : [];
        const sorted = commentary.slice().sort(compareCommentaryEventsChronologically);
        return fanOutNarrative(sorted).filter((event) => !isAbsorbedFeedEvent(event));
    }, [summaryData]);

    const bandIdentities = useMemo(() => buildPeriodBandIdentitySet(fannedEvents), [fannedEvents]);
    const scorers = useMemo(() => deriveScorers(fannedEvents), [fannedEvents]);

    // Loading state
    if (isLoading) {
        return (
            <div className="page-match-x">
                <div className="wrapper">
                    <div className="mx-empty">Loading match summary, please wait...</div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="page-match-x">
                <div className="wrapper">
                    <div className="mx-empty">
                        <p>Error loading match summary: {error.message}</p>
                        <Link to={matchesLink} className="mx-cta mx-cta--ghost">Back to Matches</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Handle case where summary is not yet available (e.g. backend returned 400)
    if (!summaryData) {
        return (
            <div className="page-match-x">
                <div className="wrapper">
                    <div className="mx-empty">
                        <p>The summary will become available once the match has finished processing. Please check back later.</p>
                        <Link to={matchesLink} className="mx-cta mx-cta--ghost">Back to Matches</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Extract data from API responses
    const { match } = summaryData;
    const playerStats = playerStatsData || summaryData.playerStats || { homeTeam: [], awayTeam: [] };
    const teamOne = match.homeTeam;
    const teamTwo = match.awayTeam;

    // Determine which team's players to show based on teamId parameter
    const selectedTeamId = teamId ? teamId : teamOne._id;
    const isHomeTeamSelected = selectedTeamId === teamOne._id;
    const players = isHomeTeamSelected ? playerStats.homeTeam : playerStats.awayTeam;

    // Transform player data to match expected format for PlayersList component
    const transformedPlayers = players.map(player => ({
        id: player.wyId || player.playerId,
        position: player.position,
        name: player.name,
        rating: player.rating ? player.rating.toFixed(1) : '0.0',
        ratingRaw: typeof player.ratingRaw === 'number' ? player.ratingRaw.toFixed(1) : undefined,
        minutes: player.minutesPlayed || 0,
        saves: player.saves || 0,
        keyTackles: player.tackles || 0,
        keyPasses: player.keyPasses ?? player.key_passes ?? player.keypasses ?? 0,
        assists: player.assists || 0,
        shots: player.shots || 0,
        goals: player.goals || 0,
        points: player.points || 0,
        fitness: player.finalFitness ?? 100,
        redCards: player.redcards ?? player.redCards ?? player.red_cards ?? 0
    }));

    const score = { home: match.homeScore, away: match.awayScore, isPlaceholder: false };
    const statusChip = { tone: 'ft', text: 'FULL-TIME' };
    const headerMeta = (
        <>
            {formatKickoffText(match.date)}
            <span className="mx-header__status"> (Completed)</span>
        </>
    );

    // The summary tab is this page; every other tab lives on the match page.
    const handleSelectTab = (nextTab) => {
        if (nextTab === MATCH_TAB.SUMMARY) return;
        navigate(`${routeBase}/match/${id}/match`);
    };

    const teamSummaryHref = (team) => `${routeBase}/match/${id}/summary/${team._id}`;
    const isKeyOnly = feedFilter === FEED_FILTER.KEY;

    return (
        <div className="page-match-x">
            <div className="wrapper">
                <MatchHeader
                    teamOne={teamOne}
                    teamTwo={teamTwo}
                    score={score}
                    statusChip={statusChip}
                    scorers={scorers}
                    pressure={null}
                    isCompact={isHeaderCompact}
                    metaText={headerMeta}
                    connectionStatus="idle"
                    isLiveMatch={false}
                />

                <div className="mx-body mx-body--single">
                    <main className="mx-main">
                        <MatchTabs
                            activeTab={MATCH_TAB.SUMMARY}
                            onSelectTab={handleSelectTab}
                            isSummaryUnlocked={true}
                        />

                        <section className="mx-summary-section">
                            <h2 className="mx-summary-section__title">Player statistics</h2>

                            <nav className="mx-tabs" aria-label="Team statistics selector">
                                <Link
                                    to={teamSummaryHref(teamOne)}
                                    className={isHomeTeamSelected ? 'is-active' : undefined}
                                    aria-current={isHomeTeamSelected ? 'page' : undefined}
                                >
                                    <span className="mx-tabs__label">{teamOne.name}</span>
                                </Link>
                                <Link
                                    to={teamSummaryHref(teamTwo)}
                                    className={!isHomeTeamSelected ? 'is-active' : undefined}
                                    aria-current={!isHomeTeamSelected ? 'page' : undefined}
                                >
                                    <span className="mx-tabs__label">{teamTwo.name}</span>
                                </Link>
                            </nav>

                            {transformedPlayers.length > 0 ? (
                                <div className="mx-summary-table">
                                    <PlayersList players={transformedPlayers} />
                                </div>
                            ) : (
                                <div className="mx-empty">No player statistics available for this team.</div>
                            )}
                        </section>

                        <section className="mx-summary-section">
                            <h2 className="mx-summary-section__title">Match commentary</h2>

                            {fannedEvents.length > 0 ? (
                                <div
                                    className={`mx-feed${isKeyOnly ? ' mx-feed--key-only' : ''}`}
                                    data-testid="summary-feed"
                                >
                                    <div className="mx-filters" role="group" aria-label="Commentary filter">
                                        <button
                                            type="button"
                                            className={feedFilter === FEED_FILTER.ALL ? 'is-active' : undefined}
                                            aria-pressed={feedFilter === FEED_FILTER.ALL}
                                            onClick={() => setFeedFilter(FEED_FILTER.ALL)}
                                        >
                                            All commentary
                                        </button>
                                        <button
                                            type="button"
                                            className={isKeyOnly ? 'is-active' : undefined}
                                            aria-pressed={isKeyOnly}
                                            onClick={() => setFeedFilter(FEED_FILTER.KEY)}
                                        >
                                            Key moments
                                        </button>
                                    </div>
                                    <div className="mx-feed-list">
                                        {fannedEvents.map((event) => (
                                            <CommentaryRow
                                                key={getCommentaryKey(event)}
                                                event={event}
                                                isBand={bandIdentities.has(getFeedEventIdentity(event)) && !event.sub_type}
                                                teamOne={teamOne}
                                                teamTwo={teamTwo}
                                                isNew={false}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="mx-empty">No commentary available for this match.</div>
                            )}
                        </section>

                        <MatchSharePanel
                            match={match}
                            scorers={scorers}
                            shareUrl={buildMatchLink(id, `summary/${teamOne._id}`, routeBase)}
                        />
                    </main>
                </div>
            </div>
        </div>
    )
}

export {MatchSummary}
